import { useState, useEffect, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { clearForUser } from "@/modules/chat/lib/handleStore";
import { useSpaceStore } from "@/modules/spaces/space-store";

interface User {
  id: string;
  email: string;
  name: string;
  role: "admin" | "user";
  status: "pending" | "approved" | "rejected";
}

interface AuthState {
  token: string | null;
  user: User | null;
  loading: boolean;
}

function loadInitial(): AuthState {
  const token = localStorage.getItem("token");
  const raw = localStorage.getItem("user");
  const user = raw ? (JSON.parse(raw) as User) : null;
  return { token, user, loading: !!token };
}

export function useAuth() {
  const [state, setState] = useState<AuthState>(loadInitial);
  const qc = useQueryClient();

  // Wipes per-user client state. Called on login (before establishing a new
  // session) and on logout. Without this, a second user logging in on the
  // same tab sees the previous user's cached query data (agents, skills,
  // sessions) until a manual refresh.
  const resetUserState = useCallback(() => {
    qc.clear();
    // Drop persisted folder handles for the departing user so a second
    // account logging in on the same browser can't load them by sessionId.
    try {
      const raw = localStorage.getItem("user");
      if (raw) {
        const prev = JSON.parse(raw) as { id?: string };
        if (prev.id) void clearForUser(prev.id);
      }
    } catch {}
    // Forget the previous user's active space — otherwise the next
    // account on this browser hits 403 on every space-scoped request
    // until they manually pick one from the sidebar.
    useSpaceStore.getState().reset();
  }, [qc]);

  useEffect(() => {
    if (!state.token) {
      setState((s) => ({ ...s, loading: false }));
      return;
    }
    fetch("/api/auth/me", {
      headers: { Authorization: `Bearer ${state.token}` },
    })
      .then(async (resp) => {
        if (!resp.ok) throw new Error("auth");
        const user = (await resp.json()) as User;
        localStorage.setItem("user", JSON.stringify(user));
        setState({ token: state.token, user, loading: false });
      })
      .catch(() => {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        setState({ token: null, user: null, loading: false });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const resp = await fetch("/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    if (!resp.ok) {
      const data = await resp.json().catch(() => ({}));
      throw new Error(data.detail || "Не удалось войти");
    }
    const { token, user } = (await resp.json()) as { token: string; user: User };
    resetUserState();
    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(user));
    setState({ token, user, loading: false });
  }, [resetUserState]);

  const register = useCallback(async (name: string, email: string, password: string) => {
    const resp = await fetch("/api/auth/register", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, email, password }),
    });
    if (!resp.ok) {
      const data = await resp.json().catch(() => ({}));
      throw new Error(data.detail || "Не удалось зарегистрироваться");
    }
    const data = (await resp.json()) as { message: string };
    return data.message;
  }, []);

  const logout = useCallback(() => {
    resetUserState();
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setState({ token: null, user: null, loading: false });
  }, [resetUserState]);

  return {
    token: state.token,
    user: state.user,
    loading: state.loading,
    isAuthenticated: !!state.token && !!state.user,
    login,
    register,
    logout,
  };
}
